import React, { useEffect, useState } from "react";
import { History, ChevronUp, ChevronDown, User, Clock } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import "./AuditHistory.css";

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const datePart = `${mm}-${dd}-${d.getFullYear()}`;
  const timePart = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${datePart} ${timePart}`;
}

// Full timestamp with seconds, shown only in the hover tooltip.
function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatFieldName(field) {
  if (!field) return "";
  const label = field.includes(".") ? field.split(".").pop() : field;
  return label.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Marks a diff cell as not applicable to this row (e.g. the "From" side of
// an Added row) rather than a real empty value, so it renders as a neutral
// blank instead of a "—" that would read as "there was nothing here before."
const NOT_APPLICABLE = Symbol("n/a");

function formatValue(value) {
  if (value === NOT_APPLICABLE) return "";
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

// Recursively walks two same-shaped objects (e.g. a Permissions blob) and
// returns only the leaf paths whose values actually differ, so an edit to
// one nested flag doesn't render every unchanged sibling flag as well.
function diffLeaves(from, to, path = []) {
  if (isPlainObject(from) || isPlainObject(to)) {
    const keys = new Set([
      ...Object.keys(from || {}),
      ...Object.keys(to || {}),
    ]);
    let leaves = [];
    keys.forEach((key) => {
      leaves = leaves.concat(
        diffLeaves((from || {})[key], (to || {})[key], [...path, key])
      );
    });
    return leaves;
  }
  const changed =
    Array.isArray(from) || Array.isArray(to)
      ? JSON.stringify(from) !== JSON.stringify(to)
      : from !== to;
  return changed ? [{ path, from, to }] : [];
}

function formatFieldPath(path) {
  return path.map(formatFieldName).join(" › ");
}

// Expands a Field/From/To row into one row per changed leaf when both sides
// are objects, rather than dumping the entire nested value as raw JSON.
// `fieldLabels` overrides the auto-titleized label for a top-level field
// (e.g. "approval_team_uuid" -> "Approval Team"); `valueFormatters` maps a
// top-level field to a function transforming its raw (scalar) value into a
// display value (e.g. resolving a uuid to a human-readable name) before the
// generic formatValue takes over for null/boolean/etc. handling.
function expandFieldDiff(f, fieldLabels = {}, valueFormatters = {}) {
  const label = fieldLabels[f.field] || formatFieldName(f.field);
  // A list field (e.g. team Members) re-sends its entire membership on every
  // edit, so a single From/To row would show every member on both sides —
  // making one add look like a full-membership replacement. Instead, emit a
  // separate "Added"/"Removed" row per direction of change, each touching
  // only the relevant column — the other column is marked N/A rather than
  // "—", so it doesn't read as "there were no members before."
  if (Array.isArray(f.from) || Array.isArray(f.to)) {
    const fromArr = Array.isArray(f.from) ? f.from : [];
    const toArr = Array.isArray(f.to) ? f.to : [];
    const removed = fromArr.filter((v) => !toArr.includes(v));
    const added = toArr.filter((v) => !fromArr.includes(v));
    const rows = [];
    if (added.length) {
      rows.push({
        label: `${label} Added`,
        from: NOT_APPLICABLE,
        to: added,
      });
    }
    if (removed.length) {
      rows.push({
        label: `${label} Removed`,
        from: removed,
        to: NOT_APPLICABLE,
      });
    }
    return rows.length ? rows : [{ label, from: [], to: [] }];
  }
  if (!isPlainObject(f.from) && !isPlainObject(f.to)) {
    const formatter = valueFormatters[f.field];
    return [
      {
        label,
        from: formatter ? formatter(f.from) : f.from,
        to: formatter ? formatter(f.to) : f.to,
      },
    ];
  }
  const leaves = diffLeaves(f.from, f.to);
  if (!leaves.length) {
    return [{ label, from: f.from, to: f.to }];
  }
  return leaves.map((leaf) => ({
    label: `${label} › ${formatFieldPath(leaf.path)}`,
    from: leaf.from,
    to: leaf.to,
  }));
}

// Groups the raw history entries (one per changed field) into per-action
// timeline entries — every field changed by the same update call shares the
// same created_at/actor, and should render as one "Edited" entry with
// multiple Field/From/To rows rather than one row per field.
function groupHistory(history) {
  const groups = [];
  (history || []).forEach((event) => {
    if (event.deleted_at) {
      groups.push({
        type: "deleted",
        actor: event.actor,
        name: event.name,
        at: event.deleted_at,
        fields: [],
      });
      return;
    }
    const key = `${event.created_at}|${event.actor}`;
    let group = groups.find((g) => g.type === "edited" && g.key === key);
    if (!group) {
      group = {
        type: "edited",
        key,
        actor: event.actor,
        name: event.name,
        at: event.created_at,
        fields: [],
      };
      groups.push(group);
    }
    group.fields.push({
      field: event.new_value?.field ?? event.previous_value?.field,
      from: event.previous_value?.value,
      to: event.new_value?.value,
    });
  });
  return groups;
}

const BADGE_LABEL = {
  created: "Created",
  edited: "Edited",
  deleted: "Deleted",
};

/**
 * AuditHistory Component
 *
 * Collapsible timeline of create/edit/delete actions for a Role, Company
 * User, or Access Group, backed by api_logging's audit endpoint.
 *
 * @param {String} resource - One of "roles", "company_users", "groups"
 * @param {String} uuid - The entity's uuid
 * @param {*} refreshKey - Optional value; changing it re-fetches the history
 * @param {String} createdAt - The entity's own created_at, used for the
 *   "created" timeline entry in place of the audit log row's created_at
 *   (which, for entities that predate audit logging, only reflects when
 *   the baseline audit row was lazily created on first edit)
 */
export default function AuditHistory({
  resource,
  uuid,
  refreshKey,
  createdAt,
  service = "identity",
  fieldLabels = {},
  valueFormatters = {},
}) {
  const [loading, setLoading] = useState(true);
  const [auditLog, setAuditLog] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [sectionOpen, setSectionOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!resource || !uuid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    FieldConfigurationAPI.getAuditHistory(resource, uuid, service).then(
      (res) => {
        if (cancelled) return;
        setAuditLog(res.status === 200 ? res.data : null);
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [resource, uuid, refreshKey, service]);

  if (loading) return null;

  const entries = auditLog ? groupHistory(auditLog.history) : [];
  entries.sort((a, b) => new Date(b.at) - new Date(a.at));
  if (auditLog) {
    entries.push({
      type: "created",
      actor: auditLog.created_by,
      name: auditLog.created_by_name,
      at: createdAt || auditLog.created_at,
      fields: [],
      title: `${
        resource === "groups"
          ? "Access group"
          : resource === "roles"
          ? "Role"
          : resource === "approval_teams"
          ? "Approval team"
          : "User"
      } created`,
    });
  }

  const isOpen = (entry, index) =>
    openKey === (entry.key ?? `${entry.type}-${index}`);

  return (
    <div className="role-detail__section audit-history">
      <div
        className="audit-history__header audit-history__header--toggle"
        onClick={() => setSectionOpen(!sectionOpen)}
        role="button"
      >
        <h3 className="role-detail__section-title audit-history__title">
          <History size={18} />
          Audit History
          <span className="audit-history__count">
            ({entries.length} {entries.length === 1 ? "entry" : "entries"})
          </span>
        </h3>
        {sectionOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>

      {sectionOpen && entries.length === 0 && (
        <div className="audit-history__empty">No history recorded yet.</div>
      )}

      {sectionOpen && (
        <div className="audit-history__timeline">
          {entries.map((entry, index) => {
            const entryKey = entry.key ?? `${entry.type}-${index}`;
            const open = isOpen(entry, index);
            const hasFields = entry.fields && entry.fields.length > 0;
            return (
              <div className="audit-history__item" key={entryKey}>
                <span
                  className={`audit-history__dot audit-history__dot--${entry.type}`}
                />
                <div className="audit-history__item-content">
                  <div
                    className="audit-history__item-header"
                    onClick={() =>
                      hasFields && setOpenKey(open ? "__none__" : entryKey)
                    }
                    role={hasFields ? "button" : undefined}
                  >
                    <span
                      className={`audit-history__badge audit-history__badge--${entry.type}`}
                    >
                      {BADGE_LABEL[entry.type]}
                    </span>
                    <span className="audit-history__item-title">
                      {entry.title ||
                        (hasFields
                          ? entry.fields
                              .map(
                                (f) =>
                                  fieldLabels[f.field] ||
                                  formatFieldName(f.field)
                              )
                              .join(", ")
                          : "")}
                    </span>
                    <span className="audit-history__meta">
                      <User size={13} />
                      {entry.name || "Unknown"}
                      <Clock size={13} />
                      <span title={formatDateTime(entry.at)}>
                        {formatDate(entry.at)}
                      </span>
                    </span>
                    {hasFields &&
                      (open ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      ))}
                  </div>
                  {hasFields && open && (
                    <table className="audit-history__diff-table">
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>From</th>
                          <th>To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entry.fields
                          .flatMap((f) =>
                            expandFieldDiff(f, fieldLabels, valueFormatters)
                          )
                          .map((row, i) => (
                            <tr key={i}>
                              <td className="audit-history__diff-field">
                                {row.label}
                              </td>
                              <td
                                className={
                                  row.from === NOT_APPLICABLE
                                    ? ""
                                    : "audit-history__diff-from"
                                }
                              >
                                {formatValue(row.from)}
                              </td>
                              <td
                                className={
                                  row.to === NOT_APPLICABLE
                                    ? ""
                                    : "audit-history__diff-to"
                                }
                              >
                                {formatValue(row.to)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
